import { filter, switchMap, take, debounceTime } from 'rxjs/operators';
import moment from 'moment';
import {
  HomeAction,
  HomeActionTypes,
  HomeState,
  HomeTabs,
} from '../../types/home';
import {
  Resource,
  ResourceStatus,
  RESOURCE_IDLE,
  RESOURCE_LOADING,
} from '../../types/resource';
import { actions } from '../index';

import {
  serializeListViewState,
  updateQueryString,
} from '../../utils/rx-store-serialization';

import createRxStore, { rxDispatch, ofType } from '..';
import {
  fetchHomeDpuSearchResults,
  fetchHomeOrganizationSearchResults,
} from '../../rx-actions/load-home';
import { getRequiredSessionToken } from '../../utils/session-token';
import { SORT_EMPTY } from '../../types/sort';

export const initialState: HomeState = {
  filters: [],
  searchText: '',
  sort: SORT_EMPTY,

  organizationResource: RESOURCE_IDLE,
  dpuResource: RESOURCE_IDLE,

  activeTab: HomeTabs.DPU_SEARCH,
  search: '',
};

function reducer(state: HomeState, action: HomeAction): HomeState {
  switch (action.type) {
    case HomeActionTypes.HOME_LOAD_ORGANIZATIONS_START:
      return {
        ...state,
        organizationResource:
          state.organizationResource.status !== ResourceStatus.COMPLETE
            ? RESOURCE_LOADING
            : state.organizationResource,
      };
    case HomeActionTypes.HOME_LOAD_ORGANIZATIONS_ERROR:
      return {
        ...state,
        organizationResource: {
          status: ResourceStatus.ERROR,
          error: action.error,
        },
      };
    case HomeActionTypes.HOME_LOAD_ORGANIZATIONS_COMPLETE:
      return {
        ...state,
        organizationResource: {
          status: ResourceStatus.COMPLETE,
          data: { organizations: action.organizations },
        },
      };

    case HomeActionTypes.HOME_LOAD_DPUS_START:
      return {
        ...state,
        dpuResource:
          state.dpuResource.status !== ResourceStatus.COMPLETE
            ? RESOURCE_LOADING
            : state.dpuResource,
      };
    case HomeActionTypes.HOME_LOAD_DPUS_ERROR:
      return {
        ...state,
        dpuResource: {
          status: ResourceStatus.ERROR,
          error: action.error,
        },
      };
    case HomeActionTypes.HOME_LOAD_DPUS_COMPLETE:
      return {
        ...state,
        dpuResource: {
          status: ResourceStatus.COMPLETE,
          data: { units: action.dpus },
        },
      };

    case HomeActionTypes.HOME_CHANGE_TAB:
      return { ...state, activeTab: action.tab, search: '' };
    case HomeActionTypes.HOME_CHANGE_SEARCH:
      return { ...state, search: action.search };

    default:
      return state;
  }
}

const HomeStore = createRxStore('HomeStore', initialState, reducer);

// =================================
// SIDE EFFECT: on initial load of the list view, figure out what request needs to be made and make it to
// load the data for the right view.
// =================================
actions
  .pipe(
    ofType(HomeActionTypes.HOME_CHANGE_PAGE),
    switchMap(() => HomeStore.pipe(take(1))),
  )
  .subscribe((state) => {
    const jwtToken = getRequiredSessionToken();
    fetchHomeDpuSearchResults(rxDispatch, jwtToken, '');
    fetchHomeOrganizationSearchResults(rxDispatch, jwtToken, '');
  });

// =================================
// SIDE EFFECT: when the dpu search results change, re-request the search data.
// =================================
actions
  .pipe(
    ofType<{ search: string }>(HomeActionTypes.HOME_CHANGE_SEARCH),
    debounceTime(100),
    switchMap(
      () => HomeStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
    filter(({ action, state }) => state.activeTab === HomeTabs.DPU_SEARCH),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    fetchHomeDpuSearchResults(rxDispatch, jwtToken, action.search);
  });

export default HomeStore;
